import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Star,
  User,
  Clock,
  Mail,
  Phone,
  MapPin,
  Calendar,
  ArrowLeft,
  CheckCircle,
  XCircle,
  Award,
  TrendingUp,
  Activity,
  Users,
  DollarSign,
  BookOpen,
  MessageSquare,
  Edit,
  Shield,
  AlertCircle,
  Eye,
  Download,
  Filter,
  Search,
  ChevronLeft,
  ChevronRight,
  CalendarDays,
  FileText,
  Upload,
  ExternalLink
} from 'lucide-react'
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, parseISO, isToday, isSameMonth } from 'date-fns'
import ApiService from '../services/api'
import toast from 'react-hot-toast'

function PanditDetails() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [pandit, setPandit] = useState(null)
  const [bookings, setBookings] = useState([])
  const [documents, setDocuments] = useState([])
  const [stats, setStats] = useState({
    totalBookings: 0,
    completedBookings: 0,
    pendingBookings: 0,
    cancelledBookings: 0,
    totalRevenue: 0,
    averageRating: 0,
    totalReviews: 0
  })
  const [loading, setLoading] = useState(true)
  const [bookingsLoading, setBookingsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [activeTab, setActiveTab] = useState('overview')
  const [bookingFilter, setBookingFilter] = useState('all')
  const [bookingSearch, setBookingSearch] = useState('')
  const [currentMonth, setCurrentMonth] = useState(new Date())
  const [bookedSlots, setBookedSlots] = useState([])

  // Action loading states
  const [verifyingPandit, setVerifyingPandit] = useState(false)
  const [featuringPandit, setFeaturingPandit] = useState(false)

  const [approvingDocument, setApprovingDocument] = useState(null) // documentId being approved/rejected
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false)
  const [rejectionReason, setRejectionReason] = useState('')
  const [rejectingPandit, setRejectingPandit] = useState(false)

  // Commission State
  const [globalCommission, setGlobalCommission] = useState(20);
  const [commissionForm, setCommissionForm] = useState({ percentage: '', type: 'percentage' });
  const [commissionLoading, setCommissionLoading] = useState(false);
  const [isCommissionSaving, setIsCommissionSaving] = useState(false);

  useEffect(() => {
    if (id) {
      fetchPanditDetails()
      // fetchPanditBookings() - Now called automatically in fetchPanditDetails if needed
    }
    if (id) {
      fetchPanditDetails()
      fetchCommissionData()
      // fetchPanditBookings() - Now called automatically in fetchPanditDetails if needed
    }
  }, [id])

  const fetchCommissionData = async () => {
    try {
      const globalRes = await ApiService.getGlobalCommission();
      if (globalRes.success) setGlobalCommission(globalRes.data.percentage);

      const panditRes = await ApiService.getPanditCommission(id);
      if (panditRes.success && panditRes.data) {
        setCommissionForm({
          percentage: panditRes.data.commissionPercentage !== null ? panditRes.data.commissionPercentage : '',
          type: panditRes.data.commissionType || 'percentage'
        });
      }
    } catch (err) {
      console.error("Failed to fetch commission data", err);
    }
  };

  const handleUpdateCommission = async (reset = false) => {
    try {
      setIsCommissionSaving(true);

      let percentageVal = commissionForm.percentage;
      if (percentageVal === '' || percentageVal === undefined) percentageVal = null;

      const typeVal = commissionForm.type || 'percentage';

      console.log('Sending Commission Update:', {
        id,
        reset,
        percentage: reset ? null : percentageVal,
        type: reset ? 'percentage' : typeVal
      });

      if (reset) {
        await ApiService.updatePanditCommission(id, null, 'percentage');
      } else {
        await ApiService.updatePanditCommission(id, percentageVal, typeVal);
      }

      toast.success(reset ? "Reverted to Global Commission" : "Commission Updated");
      // Refresh data to reflect changes
      fetchCommissionData();
      fetchPanditDetails();
    } catch (err) {
      console.error('Update Commission Error:', err);
      toast.error(err.message || "Failed to update commission");
    } finally {
      setIsCommissionSaving(false);
    }
  };

  const fetchPanditDetails = async () => {
    try {
      setLoading(true)
      setError(null)

      // Use the new comprehensive admin API endpoint
      const response = await ApiService.getPanditDetailsById(id)
      setPandit(response.data)

      // Pre-populate commission form if data is available in the main response
      // This avoids a logic gap if the financials tab is opened before fetchCommissionData completes
      if (response.data.financials) {
        setCommissionForm({
          percentage: response.data.financials.commissionPercentage !== null ? response.data.financials.commissionPercentage : '',
          type: response.data.financials.commissionType || 'percentage'
        });
      }

      // Set bookings from the API response
      if (response.data.bookings) {
        setBookings(response.data.bookings)
        setBookingsLoading(false)
      }

      // Set blocked slots from the API response
      if (response.data.blockedSlots) {
        setBookedSlots(response.data.blockedSlots)
      }

      // Set documents from the API response
      if (response.data.documents) {
        setDocuments(response.data.documents)
      }

      // Calculate stats from actual booking data with mock fallback
      const bookings = response.data.bookings || []
      const completedBookings = bookings.filter(b =>
        b.status?.toLowerCase() === 'completed' ||
        b.status?.toLowerCase() === 'confirmed'
      ).length
      const pendingBookings = bookings.filter(b =>
        b.status?.toLowerCase() === 'requested' ||
        b.status?.toLowerCase() === 'pending'
      ).length
      const cancelledBookings = bookings.filter(b =>
        b.status?.toLowerCase() === 'cancelled' ||
        b.status?.toLowerCase() === 'rejected'
      ).length

      // Calculate total revenue from confirmed/completed bookings
      const totalRevenue = bookings
        .filter(b =>
          (b.status?.toLowerCase() === 'confirmed' || b.status?.toLowerCase() === 'completed') &&
          b.totalAmount
        )
        .reduce((sum, booking) => sum + parseFloat(booking.totalAmount || 0), 0)

      const panditStats = {
        totalBookings: bookings.length || 0,
        completedBookings: completedBookings || 0,
        pendingBookings: pendingBookings || 0,
        cancelledBookings: cancelledBookings || 0,
        totalRevenue: totalRevenue || 0,
        averageRating: response.data.profile?.ratingAvg || 0,
        totalReviews: response.data.profile?.ratingCount || 0
      }
      setStats(panditStats)
    } catch (error) {
      console.error('Error fetching pandit details:', error)
      setError(error.message || 'Failed to load pandit details')
      toast.error('Failed to load pandit details')

      // Fallback to old API if new one fails
      try {
        const fallbackResponse = await ApiService.getPanditById(id)
        setPandit(fallbackResponse.data)

        // Use empty stats for fallback
        const panditStats = {
          totalBookings: 0,
          completedBookings: 0,
          pendingBookings: 0,
          cancelledBookings: 0,
          totalRevenue: 0,
          averageRating: fallbackResponse.data.profile?.ratingAvg || 0,
          totalReviews: fallbackResponse.data.profile?.ratingCount || 0
        }
        setStats(panditStats)
      } catch (fallbackError) {
        console.error('Fallback API also failed:', fallbackError)
      }
    } finally {
      setLoading(false)
    }
  }

  const fetchPanditBookings = async () => {
    try {
      setBookingsLoading(true)

      // Bookings are now fetched in fetchPanditDetails via getPanditDetailsById
      // This function is kept for backward compatibility and fallback scenarios

      // If bookings are already loaded from the main API call, skip this
      if (bookings.length > 0) {
        setBookingsLoading(false)
        return
      }

      // Fallback: Try to fetch bookings separately if main API didn't provide them
      try {
        const response = await ApiService.getPanditBookingsById(id)
        setBookings(response.data || [])
      } catch (specificError) {
        // Fallback to general bookings endpoint and filter
        console.log('Specific endpoint not available, using fallback')
        const response = await ApiService.getAllBookings()
        const panditBookings = response.data?.filter(booking =>
          booking.panditId === id || booking.pandit?.id === id
        ) || []
        setBookings(panditBookings)
      }
    } catch (error) {
      console.error('Error fetching pandit bookings:', error)
      toast.error('Failed to load bookings')
    } finally {
      setBookingsLoading(false)
    }
  }

  // Calendar helper functions
  const getDaysInMonth = (date) => {
    const start = startOfMonth(date)
    const end = endOfMonth(date)
    return eachDayOfInterval({ start, end })
  }

  const isSlotBooked = (date) => {
    return bookedSlots.some(slot => {
      const [startTime] = slot.split('-')
      const slotDate = parseISO(startTime)
      return isSameDay(date, slotDate)
    })
  }

  const getBookingsForDate = (date) => {
    return bookings.filter(booking => {
      const bookingDate = parseISO(booking.date)
      return isSameDay(date, bookingDate)
    })
  }

  const navigateMonth = (direction) => {
    const newDate = new Date(currentMonth)
    newDate.setMonth(currentMonth.getMonth() + direction)
    setCurrentMonth(newDate)
  }

  const handleVerifyPandit = async () => {
    try {
      setVerifyingPandit(true)
      await ApiService.verifyPandit(id)
      toast.success('Pandit verified successfully')
      fetchPanditDetails() // Refresh data
    } catch (error) {
      console.error('Error verifying pandit:', error)
      toast.error('Failed to verify pandit')
    } finally {
      setVerifyingPandit(false)
    }
  }

  const handleRejectPandit = async () => {
    if (!rejectionReason.trim()) {
      toast.error('Please provide a reason for rejection')
      return
    }

    try {
      setRejectingPandit(true)
      await ApiService.rejectPanditApplication(id, rejectionReason)
      toast.success('Pandit rejected successfully')
      setIsRejectModalOpen(false)
      fetchPanditDetails() // Refresh data
    } catch (error) {
      console.error('Error rejecting pandit:', error)
      toast.error('Failed to reject pandit')
    } finally {
      setRejectingPandit(false)
    }
  }

  const handleFeaturePandit = async (featured) => {
    try {
      setFeaturingPandit(true)
      await ApiService.updatePandit(id, { featuredPandit: featured })
      toast.success(`Pandit ${featured ? 'featured' : 'unfeatured'} successfully`)
      fetchPanditDetails() // Refresh data
    } catch (error) {
      console.error('Error updating pandit featured status:', error)
      toast.error('Failed to update pandit status')
    } finally {
      setFeaturingPandit(false)
    }
  }

  const handleApproveDocument = async (documentId, status) => {
    try {
      setApprovingDocument(documentId)
      await ApiService.approveDocument(documentId, status)
      toast.success(`Document ${status} successfully`)
      fetchPanditDetails() // Refresh data to show updated status
    } catch (error) {
      console.error('Error approving/rejecting document:', error)
      toast.error(`Failed to ${status} document`)
    } finally {
      setApprovingDocument(null)
    }
  }

  const filteredBookings = bookings.filter(booking => {
    const matchesFilter = bookingFilter === 'all' || booking.status?.toLowerCase() === bookingFilter
    const matchesSearch = !bookingSearch ||
      booking.service?.name?.toLowerCase().includes(bookingSearch.toLowerCase()) ||
      booking.user?.email?.toLowerCase().includes(bookingSearch.toLowerCase())
    return matchesFilter && matchesSearch
  })

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'confirmed': return 'bg-green-100 text-green-800'
      case 'completed': return 'bg-blue-100 text-blue-800'
      case 'requested': return 'bg-yellow-100 text-yellow-800'
      case 'pending': return 'bg-orange-100 text-orange-800'
      case 'cancelled': return 'bg-red-100 text-red-800'
      case 'rejected': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getPaymentStatusColor = (paymentStatus) => {
    switch (paymentStatus?.toLowerCase()) {
      case 'paid': return 'bg-green-100 text-green-800'
      case 'pending': return 'bg-yellow-100 text-yellow-800'
      case 'failed': return 'bg-red-100 text-red-800'
      case 'refunded': return 'bg-blue-100 text-blue-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
      </div>
    )
  }

  if (error || !pandit) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Error Loading Pandit</h2>
          <p className="text-gray-600 mb-4">{error || 'Pandit not found'}</p>
          <button
            onClick={() => navigate('/pandits')}
            className="inline-flex items-center px-4 py-2 bg-orange-600 text-white font-medium rounded-lg hover:bg-orange-700"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Pandits
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/pandits')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Pandit Details</h1>
            <p className="text-gray-600">Complete profile and activity overview</p>
          </div>
        </div>
        <div className="flex space-x-3">
          {!pandit.isVerified && (
            <button
              onClick={handleVerifyPandit}
              disabled={verifyingPandit}
              className="inline-flex items-center px-4 py-2 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {verifyingPandit ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              ) : (
                <CheckCircle className="w-4 h-4 mr-2" />
              )}
              {verifyingPandit ? 'Verifying...' : 'Verify Pandit'}
            </button>
          )}
          <button
            onClick={() => handleFeaturePandit(!pandit.featuredPandit)}
            disabled={featuringPandit}
            className={`inline-flex items-center px-4 py-2 font-medium rounded-lg disabled:opacity-50 disabled:cursor-not-allowed ${pandit.featuredPandit
              ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'
              : 'bg-yellow-600 text-white hover:bg-yellow-700'
              }`}
          >
            {featuringPandit ? (
              <div className={`animate-spin rounded-full h-4 w-4 border-b-2 mr-2 ${pandit.featuredPandit ? 'border-yellow-800' : 'border-white'
                }`}></div>
            ) : (
              <Star className="w-4 h-4 mr-2" />
            )}
            {featuringPandit
              ? 'Updating...'
              : (pandit.featuredPandit ? 'Remove Featured' : 'Make Featured')
            }
          </button>
          {(pandit.status || pandit.user?.status) !== 'PROFILE_REJECTED' && (
            <button
              onClick={() => setIsRejectModalOpen(true)}
              className="inline-flex items-center px-4 py-2 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 ml-3"
            >
              <XCircle className="w-4 h-4 mr-2" />
              Reject
            </button>
          )}
        </div>
      </div>

      {/* Profile Overview Card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="bg-gradient-to-r from-orange-300 to-red-500 h-2"></div>
        <div className="px-6 pb-2 bg-gradient-to-r from-orange-300 to-red-500">
          <div className="flex items-start space-x-6">
            <div className="flex-shrink-0">
              <div className="h-24 w-24 rounded-full bg-white p-1 shadow-lg">
                <div className="h-full w-full rounded-full bg-gradient-to-r from-orange-300 to-red-500 flex items-center justify-center">
                  <span className="text-white font-bold text-2xl">
                    {pandit.profile?.name?.charAt(0)?.toUpperCase() || pandit.user?.email?.charAt(0)?.toUpperCase()}
                  </span>
                </div>
              </div>
            </div>
            <div className="flex-1 mt-4">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-white flex items-center">
                    {pandit.profile?.name || 'No name provided'}
                    {pandit.featuredPandit && (
                      <span className="ml-3 inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800">
                        <Star className="w-4 h-4 mr-1" />
                        Featured
                      </span>
                    )}
                    <span className="ml-2 bg-gray-50 flex items-center gap-2  p-1 rounded-full">
                      {pandit.isVerified ? (
                        <CheckCircle className="w-6 h-6 text-green-500" />
                      ) : (
                        <XCircle className="w-6 h-6 text-red-500" />
                      )}
                    </span>
                  </h2>
                  <p className="text-white">{pandit.user?.email}</p>
                  {pandit.expertise?.[0] && (
                    <p className="text-orange-600 font-medium">{pandit.expertise[0].service?.name}</p>
                  )}
                </div>
                <div className="text-right mb-6">
                  <div className="flex items-center text-yellow-200 mb-1">
                    <Star className="w-5 h-5 mr-1" />
                    <span className="text-xl font-bold text-gray-50">
                      {pandit.profile?.ratingAvg || 0}
                    </span>
                    <span className="text-white ml-1">
                      ({pandit.profile?.ratingCount || 0} reviews)
                    </span>
                  </div>
                  <p className="text-sm text-gray-100">
                    Member since {format(new Date(pandit.createdAt), 'MMM yyyy')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Profile Rejected Banner */}
      {(pandit.status || pandit.user?.status) === 'PROFILE_REJECTED' && pandit.rejectionReason && (
        <div className="bg-orange-50 border-l-4 border-orange-400 p-4 rounded-lg">
          <div className="flex">
            <div className="flex-shrink-0">
              <AlertCircle className="h-5 w-5 text-orange-400" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-orange-800">Profile Rejected</h3>
              <div className="mt-2 text-sm text-orange-700">
                <p><strong>Reason:</strong> {pandit.rejectionReason}</p>
                <p className="mt-2">This pandit can reapply by updating their profile and addressing the issues mentioned above.</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Bookings</p>
              <p className="text-3xl font-bold text-gray-900">{stats.totalBookings}</p>
            </div>
            <div className="bg-blue-500 p-3 rounded-lg">
              <BookOpen className="w-6 h-6 text-white" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Completed</p>
              <p className="text-3xl font-bold text-gray-900">{stats.completedBookings}</p>
            </div>
            <div className="bg-green-500 p-3 rounded-lg">
              <CheckCircle className="w-6 h-6 text-white" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Revenue</p>
              <p className="text-3xl font-bold text-gray-900">₹{stats.totalRevenue}</p>
            </div>
            <div className="bg-yellow-500 p-3 rounded-lg">
              <DollarSign className="w-6 h-6 text-white" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Average Rating</p>
              <p className="text-3xl font-bold text-gray-900">{stats.averageRating}</p>
            </div>
            <div className="bg-purple-500 p-3 rounded-lg">
              <Star className="w-6 h-6 text-white" />
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            {[
              { id: 'overview', label: 'Overview', icon: User },
              { id: 'documents', label: 'Documents', icon: FileText },
              { id: 'financials', label: 'Financials', icon: DollarSign },
              { id: 'bookings', label: 'Bookings', icon: Calendar },
              { id: 'calendar', label: 'Calendar', icon: CalendarDays },
              { id: 'reviews', label: 'Reviews', icon: MessageSquare }
            ].map((tab) => {
              const Icon = tab.icon
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center ${activeTab === tab.id
                    ? 'border-orange-500 text-orange-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                >
                  <Icon className="w-4 h-4 mr-2" />
                  {tab.label}
                </button>
              )
            })}
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Personal Information */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Personal Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div className="flex items-center">
                      <Mail className="w-5 h-5 text-gray-400 mr-3" />
                      <div>
                        <p className="text-sm text-gray-600">Email</p>
                        <p className="font-medium">{pandit.email || pandit.user?.email}</p>
                      </div>
                    </div>
                    {(pandit.phone || pandit.user?.phone) && (
                      <div className="flex items-center">
                        <Phone className="w-5 h-5 text-gray-400 mr-3" />
                        <div>
                          <p className="text-sm text-gray-600">Phone</p>
                          <p className="font-medium">{pandit.phone || pandit.user?.phone}</p>
                        </div>
                      </div>
                    )}
                    <div className="flex items-center">
                      <MapPin className="w-5 h-5 text-gray-400 mr-3" />
                      <div>
                        <p className="text-sm text-gray-600">Location</p>
                        <p className="font-medium">
                          {pandit.profile?.city}{pandit.profile?.state && `, ${pandit.profile.state}`}{pandit.profile?.country && `, ${pandit.profile.country}`}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <Shield className="w-5 h-5 text-gray-400 mr-3" />
                      <div>
                        <p className="text-sm text-gray-600">Verification</p>
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${pandit.isVerified
                          ? 'bg-green-100 text-green-800'
                          : 'bg-yellow-100 text-yellow-800'
                          }`}>
                          {pandit.isVerified ? 'Verified' : 'Pending'}
                        </span>
                      </div>
                    </div>
                    {pandit.profile?.fatherName && (
                      <div className="flex items-center">
                        <User className="w-5 h-5 text-gray-400 mr-3" />
                        <div>
                          <p className="text-sm text-gray-600">Father's Name</p>
                          <p className="font-medium">{pandit.profile.fatherName}</p>
                        </div>
                      </div>
                    )}
                    {pandit.profile?.motherName && (
                      <div className="flex items-center">
                        <User className="w-5 h-5 text-gray-400 mr-3" />
                        <div>
                          <p className="text-sm text-gray-600">Mother's Name</p>
                          <p className="font-medium">{pandit.profile.motherName}</p>
                        </div>
                      </div>
                    )}
                    {pandit.profile?.gotra && (
                      <div className="flex items-center">
                        <Users className="w-5 h-5 text-gray-400 mr-3" />
                        <div>
                          <p className="text-sm text-gray-600">Gotra</p>
                          <p className="font-medium">{pandit.profile.gotra}</p>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="space-y-4">
                    <div className="flex items-center">
                      <Clock className="w-5 h-5 text-gray-400 mr-3" />
                      <div>
                        <p className="text-sm text-gray-600">Experience</p>
                        <p className="font-medium">{pandit.profile?.experience || 0} years</p>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <Activity className="w-5 h-5 text-gray-400 mr-3" />
                      <div>
                        <p className="text-sm text-gray-600">Status</p>
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          (pandit.status || pandit.user?.status) === 'ACTIVE'
                            ? 'bg-green-100 text-green-800'
                            : (pandit.status || pandit.user?.status) === 'PROFILE_REJECTED'
                            ? 'bg-orange-100 text-orange-800'
                            : (pandit.status || pandit.user?.status) === 'SUSPENDED'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-gray-100 text-gray-800'
                          }`}>
                          {pandit.status || pandit.user?.status || 'UNKNOWN'}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <Award className="w-5 h-5 text-gray-400 mr-3" />
                      <div>
                        <p className="text-sm text-gray-600">Featured Pandit</p>
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${pandit.featuredPandit
                          ? 'bg-purple-100 text-purple-800'
                          : 'bg-gray-100 text-gray-800'
                          }`}>
                          {pandit.featuredPandit ? 'Yes' : 'No'}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <Calendar className="w-5 h-5 text-gray-400 mr-3" />
                      <div>
                        <p className="text-sm text-gray-600">Joined</p>
                        <p className="font-medium">{format(parseISO(pandit.createdAt), 'MMM dd, yyyy')}</p>
                      </div>
                    </div>
                    {pandit.profile?.languages && (
                      <div className="flex items-center">
                        <MessageSquare className="w-5 h-5 text-gray-400 mr-3" />
                        <div>
                          <p className="text-sm text-gray-600">Languages</p>
                          <p className="font-medium">{pandit.profile.languages.join(', ')}</p>
                        </div>
                      </div>
                    )}
                    {pandit.profile?.birthDate && (
                      <div className="flex items-center">
                        <Calendar className="w-5 h-5 text-gray-400 mr-3" />
                        <div>
                          <p className="text-sm text-gray-600">Birth Date</p>
                          <p className="font-medium">
                            {format(new Date(pandit.profile.birthDate), 'MMM dd, yyyy')}
                          </p>
                        </div>
                      </div>
                    )}
                    {pandit.profile?.birthTime && (
                      <div className="flex items-center">
                        <Clock className="w-5 h-5 text-gray-400 mr-3" />
                        <div>
                          <p className="text-sm text-gray-600">Birth Time</p>
                          <p className="font-medium">{pandit.profile.birthTime}</p>
                        </div>
                      </div>
                    )}
                    {pandit.profile?.birthPlace && (
                      <div className="flex items-center">
                        <MapPin className="w-5 h-5 text-gray-400 mr-3" />
                        <div>
                          <p className="text-sm text-gray-600">Birth Place</p>
                          <p className="font-medium">{pandit.profile.birthPlace}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Expertise */}
              {pandit.expertise && pandit.expertise.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Expertise</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {pandit.expertise.map((exp, index) => (
                      <div key={index} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50">
                        <div className="flex items-start justify-between mb-2">
                          <h4 className="font-medium text-gray-900">{exp.serviceDetails?.title}</h4>
                          <span className="text-lg font-bold text-orange-600">₹{exp.serviceDetails?.basePrice}</span>
                        </div>
                        <p className="text-sm text-gray-600 mb-2">Level: {exp.experienceLevel}</p>
                        {exp.customTitle && (
                          <p className="text-sm text-blue-600 mb-2 font-medium">{exp.customTitle}</p>
                        )}
                        {exp.serviceDetails?.description && (
                          <p className="text-sm text-gray-600 mb-2">{exp.serviceDetails.description}</p>
                        )}
                        <div className="flex items-center justify-between text-xs text-gray-500">
                          <span>Duration: {exp.serviceDetails?.durationMin} min</span>
                          <span className="capitalize">{exp.serviceDetails?.category?.name}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Pricing */}
              {pandit.pricing && pandit.pricing.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Pricing</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {pandit.pricing.map((price, index) => (
                      <div key={index} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex justify-between items-center">
                          <span className="font-medium text-gray-900">₹{price.price}</span>
                          <span className="text-sm text-gray-600">{price.durationMin} mins</span>
                        </div>
                        {price.description && (
                          <p className="text-sm text-gray-600 mt-2">{price.description}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'documents' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Verification Documents</h3>
                <div className="text-sm text-gray-600">
                  {documents.length} document{documents.length !== 1 ? 's' : ''} uploaded
                </div>
              </div>

              {documents.length === 0 ? (
                <div className="text-center py-12">
                  <Upload className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <h4 className="text-lg font-medium text-gray-900 mb-2">No Documents Found</h4>
                  <p className="text-gray-500">This pandit hasn't uploaded any verification documents yet.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {documents.map((doc) => (
                    <div key={doc.id} className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center">
                          <div className="bg-blue-100 p-2 rounded-lg mr-3">
                            <FileText className="w-5 h-5 text-blue-600" />
                          </div>
                          <div>
                            <h4 className="font-medium text-gray-900 capitalize">
                              {doc.docType.replace('_', ' ')}
                            </h4>
                            <p className="text-sm text-gray-500">
                              Version {doc.version}
                            </p>
                          </div>
                        </div>
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${doc.status === 'approved' ? 'bg-green-100 text-green-800' :
                          doc.status === 'rejected' ? 'bg-red-100 text-red-800' :
                            doc.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-gray-100 text-gray-800'
                          }`}>
                          {doc.status}
                        </span>
                      </div>

                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">Uploaded:</span>
                          <span className="text-sm font-medium text-gray-900">
                            {format(new Date(doc.createdAt), 'MMM dd, yyyy')}
                          </span>
                        </div>

                        {doc.updatedAt !== doc.createdAt && (
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-600">Updated:</span>
                            <span className="text-sm font-medium text-gray-900">
                              {format(new Date(doc.updatedAt), 'MMM dd, yyyy')}
                            </span>
                          </div>
                        )}

                        {doc.verifiedBy && (
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-600">Verified by:</span>
                            <span className="text-sm font-medium text-gray-900">
                              Admin
                            </span>
                          </div>
                        )}

                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">Status:</span>
                          <span className={`text-sm font-medium ${doc.isActive ? 'text-green-600' : 'text-red-600'
                            }`}>
                            {doc.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                      </div>

                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <div className="flex flex-col space-y-2">
                          {/* View and Download buttons */}
                          <div className="flex space-x-2">
                            <button
                              onClick={() => window.open(doc.fileUrl, '_blank')}
                              className="flex-1 inline-flex items-center justify-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                            >
                              <Eye className="w-4 h-4 mr-2" />
                              View
                            </button>
                            <button
                              onClick={() => {
                                const link = window.document.createElement('a')
                                link.href = doc.fileUrl
                                link.download = `${doc.docType}_${doc.id}`
                                link.click()
                              }}
                              className="flex-1 inline-flex items-center justify-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                            >
                              <Download className="w-4 h-4 mr-2" />
                              Download
                            </button>
                          </div>

                          {/* Approve/Reject buttons - only show if not already approved/rejected */}
                          {doc.status === 'pending' && (
                            <div className="flex space-x-2">
                              <button
                                onClick={() => handleApproveDocument(doc.id, 'approved')}
                                disabled={approvingDocument === doc.id}
                                className="flex-1 inline-flex items-center justify-center px-3 py-2 border border-green-300 rounded-md text-sm font-medium text-green-700 bg-green-50 hover:bg-green-100 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                {approvingDocument === doc.id ? (
                                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-700 mr-2"></div>
                                ) : (
                                  <CheckCircle className="w-4 h-4 mr-2" />
                                )}
                                Approve
                              </button>
                              <button
                                onClick={() => handleApproveDocument(doc.id, 'rejected')}
                                disabled={approvingDocument === doc.id}
                                className="flex-1 inline-flex items-center justify-center px-3 py-2 border border-red-300 rounded-md text-sm font-medium text-red-700 bg-red-50 hover:bg-red-100 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                {approvingDocument === doc.id ? (
                                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-700 mr-2"></div>
                                ) : (
                                  <XCircle className="w-4 h-4 mr-2" />
                                )}
                                Reject
                              </button>
                            </div>
                          )}

                          {/* Show change status button for approved/rejected documents */}
                          {(doc.status === 'approved' || doc.status === 'rejected') && (
                            <button
                              onClick={() => handleApproveDocument(doc.id, doc.status === 'approved' ? 'rejected' : 'approved')}
                              disabled={approvingDocument === doc.id}
                              className={`w-full inline-flex items-center justify-center px-3 py-2 border rounded-md text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed ${doc.status === 'approved'
                                ? 'border-red-300 text-red-700 bg-red-50 hover:bg-red-100'
                                : 'border-green-300 text-green-700 bg-green-50 hover:bg-green-100'
                                }`}
                            >
                              {approvingDocument === doc.id ? (
                                <div className={`animate-spin rounded-full h-4 w-4 border-b-2 mr-2 ${doc.status === 'approved' ? 'border-red-700' : 'border-green-700'
                                  }`}></div>
                              ) : doc.status === 'approved' ? (
                                <XCircle className="w-4 h-4 mr-2" />
                              ) : (
                                <CheckCircle className="w-4 h-4 mr-2" />
                              )}
                              {doc.status === 'approved' ? 'Reject Document' : 'Approve Document'}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Document Requirements Info */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold text-blue-900 mb-2">Required Documents for Verification</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {[
                    { type: 'aadhar', label: 'Aadhar Card', required: true },
                    { type: 'pan', label: 'PAN Card', required: true },
                    { type: 'photo', label: 'Profile Photo', required: true },
                    { type: 'education', label: 'Education Certificate', required: false },
                    { type: 'experience', label: 'Experience Certificate', required: false },
                    { type: 'other', label: 'Other Documents', required: false }
                  ].map((docReq) => {
                    const hasDoc = documents.some(doc => doc.docType === docReq.type && doc.isActive)
                    const approvedDoc = documents.find(doc => doc.docType === docReq.type && doc.status === 'approved' && doc.isActive)

                    return (
                      <div key={docReq.type} className="flex items-center space-x-2">
                        <div className={`w-3 h-3 rounded-full ${approvedDoc ? 'bg-green-500' :
                          hasDoc ? 'bg-yellow-500' :
                            'bg-gray-300'
                          }`}></div>
                        <span className={`text-sm ${docReq.required ? 'font-medium text-gray-900' : 'text-gray-700'
                          }`}>
                          {docReq.label}
                          {docReq.required && <span className="text-red-500 ml-1">*</span>}
                        </span>
                        {approvedDoc && (
                          <CheckCircle className="w-4 h-4 text-green-500" />
                        )}
                      </div>
                    )
                  })}
                </div>
                <div className="mt-3 text-xs text-blue-700">
                  <span className="font-medium">Legend:</span>
                  <span className="ml-2">🟢 Approved</span>
                  <span className="ml-2">🟡 Uploaded/Pending</span>
                  <span className="ml-2">⚪ Not Uploaded</span>
                  <span className="ml-2">* Required for verification</span>
                </div>
              </div>

              {/* Document Statistics */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-3">Document Summary</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-blue-600">
                      {documents.length}
                    </div>
                    <div className="text-sm text-gray-600">Total Documents</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-600">
                      {documents.filter(doc => doc.status === 'approved').length}
                    </div>
                    <div className="text-sm text-gray-600">Approved</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-yellow-600">
                      {documents.filter(doc => doc.status === 'pending').length}
                    </div>
                    <div className="text-sm text-gray-600">Pending</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-red-600">
                      {documents.filter(doc => doc.status === 'rejected').length}
                    </div>
                    <div className="text-sm text-gray-600">Rejected</div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'financials' && (
            <div className="space-y-6">
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Commission Settings</h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {/* Current Status */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="text-sm font-medium text-gray-500 mb-2">Effective Commission</h4>
                    <div className="flex items-baseline space-x-2">
                      <span className="text-3xl font-bold text-gray-900">
                        {commissionForm.percentage !== '' && commissionForm.percentage !== null
                          ? commissionForm.percentage
                          : globalCommission}
                        {commissionForm.type === 'flat' && commissionForm.percentage !== '' ? '' : '%'}
                      </span>
                      <span className="text-sm text-gray-500">
                        {commissionForm.type === 'flat' && commissionForm.percentage !== '' ? 'Flat Rate' : ''}
                      </span>
                    </div>
                    <div className="mt-2 text-sm">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full font-medium ${commissionForm.percentage !== '' && commissionForm.percentage !== null
                        ? 'bg-purple-100 text-purple-800'
                        : 'bg-blue-100 text-blue-800'
                        }`}>
                        {commissionForm.percentage !== '' && commissionForm.percentage !== null
                          ? 'Custom / Pandit Specific'
                          : 'Global Default'}
                      </span>
                    </div>
                    {commissionForm.percentage === '' || commissionForm.percentage === null ? (
                      <p className="text-xs text-gray-500 mt-2">
                        Using the global system default of {globalCommission}%.
                      </p>
                    ) : (
                      <p className="text-xs text-yellow-600 mt-2 flex items-center">
                        <AlertCircle className="w-3 h-3 mr-1" />
                        Overrides global default ({globalCommission}%)
                      </p>
                    )}
                  </div>

                  {/* Edit Form */}
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Commission Type</label>
                      <div className="flex space-x-4">
                        <label className="flex items-center">
                          <input
                            type="radio"
                            name="commissionType"
                            value="percentage"
                            checked={commissionForm.type === 'percentage'}
                            onChange={(e) => setCommissionForm({ ...commissionForm, type: e.target.value })}
                            className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300"
                          />
                          <span className="ml-2 text-sm text-gray-700">Percentage (%)</span>
                        </label>
                        <label className="flex items-center">
                          <input
                            type="radio"
                            name="commissionType"
                            value="flat"
                            checked={commissionForm.type === 'flat'}
                            onChange={(e) => setCommissionForm({ ...commissionForm, type: e.target.value })}
                            className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300"
                          />
                          <span className="ml-2 text-sm text-gray-700">Flat Amount (₹)</span>
                        </label>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {commissionForm.type === 'percentage' ? 'Percentage Value' : 'Amount (₹)'}
                      </label>
                      <input
                        type="number"
                        value={commissionForm.percentage}
                        onChange={(e) => setCommissionForm({ ...commissionForm, percentage: e.target.value })}
                        placeholder={commissionForm.type === 'percentage' ? "e.g. 20" : "e.g. 500"}
                        className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-orange-500 focus:border-orange-500 sm:text-sm"
                      />
                    </div>

                    <div className="flex space-x-3 pt-2">
                      <button
                        onClick={() => handleUpdateCommission(false)}
                        disabled={isCommissionSaving}
                        className="flex-1 inline-flex justify-center items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50"
                      >
                        {isCommissionSaving ? 'Saving...' : 'Save Configuration'}
                      </button>
                      {commissionForm.percentage !== '' && commissionForm.percentage !== null && (
                        <button
                          onClick={() => handleUpdateCommission(true)}
                          disabled={isCommissionSaving}
                          className="inline-flex justify-center items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
                        >
                          Revert to Global
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'bookings' && (
            <div className="space-y-6">
              {/* Booking Filters */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
                <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search bookings..."
                      value={bookingSearch}
                      onChange={(e) => setBookingSearch(e.target.value)}
                      className="pl-10 pr-4 py-2 w-full sm:w-64 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    />
                  </div>
                  <select
                    value={bookingFilter}
                    onChange={(e) => setBookingFilter(e.target.value)}
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  >
                    <option value="all">All Bookings</option>
                    <option value="requested">Requested</option>
                    <option value="confirmed">Confirmed</option>
                    <option value="pending">Pending</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
                <div className="text-sm text-gray-600">
                  {filteredBookings.length} bookings found
                </div>
              </div>

              {/* Bookings List */}
              {bookingsLoading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600 mx-auto"></div>
                  <p className="mt-2 text-gray-500">Loading bookings...</p>
                </div>
              ) : filteredBookings.length === 0 ? (
                <div className="text-center py-8">
                  <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">No bookings found</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {filteredBookings.map((booking) => (
                    <div key={booking.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-4">
                            <div>
                              <div className="flex items-center gap-2">
                                <h4 className="font-medium text-gray-900">
                                  {/* {booking.service?.name || booking.serviceId || 'Service not specified'} */}
                                </h4>
                                <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                                  {`BK${booking.id.substring(0, 8).toUpperCase()}`}
                                </span>
                              </div>
                              <p className="text-sm text-gray-600">
                                Customer: {booking.user?.name || booking.user?.email || 'N/A'}
                                <span className="ml-2 text-gray-500">
                                  (ID: {booking.user?.id
                                    ? `CL${booking.user.id.substring(0, 8).toUpperCase()}`
                                    : booking.userId
                                      ? `CL${booking.userId.substring(0, 8).toUpperCase()}`
                                      : 'N/A'})
                                </span>
                              </p>
                              <p className="text-sm text-gray-600">
                                Date: {booking.date ? format(new Date(booking.date), 'MMM dd, yyyy') : 'Not specified'}
                              </p>
                              {booking.startTime && booking.endTime && (
                                <p className="text-sm text-gray-600">
                                  Time: {format(new Date(booking.startTime), 'HH:mm')} - {format(new Date(booking.endTime), 'HH:mm')}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="text-right space-y-2">
                          <div className="flex flex-col space-y-1">
                            <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${getStatusColor(booking.status)}`}>
                              {booking.status || 'Unknown'}
                            </span>
                            {booking.paymentStatus && (
                              <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${getPaymentStatusColor(booking.paymentStatus)}`}>
                                Payment: {booking.paymentStatus}
                              </span>
                            )}
                          </div>
                          {booking.totalAmount && (
                            <p className="text-sm font-medium text-gray-900">
                              ₹{booking.totalAmount}
                            </p>
                          )}
                          {booking.slot && (
                            <p className="text-xs text-gray-500">
                              {booking.slot}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'calendar' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900">Booking Calendar</h3>
                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-2">
                    <div className="w-4 h-4 bg-red-500 rounded"></div>
                    <span className="text-sm text-gray-600">Booked Slots</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-4 h-4 bg-green-500 rounded"></div>
                    <span className="text-sm text-gray-600">Available</span>
                  </div>
                </div>
              </div>

              {/* Calendar Header */}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <button
                    onClick={() => navigateMonth(-1)}
                    className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
                  >
                    <ChevronLeft className="w-5 h-5 text-gray-600" />
                  </button>
                  <h4 className="text-lg font-semibold text-gray-900">
                    {format(currentMonth, 'MMMM yyyy')}
                  </h4>
                  <button
                    onClick={() => navigateMonth(1)}
                    className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
                  >
                    <ChevronRight className="w-5 h-5 text-gray-600" />
                  </button>
                </div>

                {/* Calendar Grid */}
                <div className="grid grid-cols-7 gap-1">
                  {/* Day Headers */}
                  {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                    <div key={day} className="p-2 text-center text-sm font-medium text-gray-500">
                      {day}
                    </div>
                  ))}

                  {/* Calendar Days */}
                  {getDaysInMonth(currentMonth).map((date) => {
                    const isBooked = isSlotBooked(date)
                    const dayBookings = getBookingsForDate(date)
                    const isCurrentDay = isToday(date)
                    const isCurrentMonth = isSameMonth(date, currentMonth)

                    return (
                      <div
                        key={date.toISOString()}
                        className={`
                          p-2 min-h-[60px] border border-gray-200 rounded-lg relative
                          ${isCurrentMonth ? 'bg-white' : 'bg-gray-100'}
                          ${isCurrentDay ? 'ring-2 ring-blue-500' : ''}
                          ${isBooked ? 'bg-red-100 border-red-300' : 'hover:bg-gray-50'}
                        `}
                      >
                        <div className={`text-sm font-medium ${isCurrentDay ? 'text-blue-600' :
                          isCurrentMonth ? 'text-gray-900' : 'text-gray-400'
                          }`}>
                          {format(date, 'd')}
                        </div>

                        {/* Booking indicators */}
                        {dayBookings.length > 0 && (
                          <div className="mt-1 space-y-1">
                            {dayBookings.slice(0, 2).map((booking, index) => (
                              <div
                                key={booking.id}
                                className={`text-xs px-1 py-0.5 rounded text-white truncate ${booking.status?.toLowerCase() === 'requested' ? 'bg-yellow-500' :
                                  booking.status?.toLowerCase() === 'confirmed' ? 'bg-green-500' :
                                    booking.status?.toLowerCase() === 'completed' ? 'bg-blue-500' :
                                      booking.status?.toLowerCase() === 'pending' ? 'bg-orange-500' :
                                        booking.status?.toLowerCase() === 'cancelled' ? 'bg-red-500' :
                                          'bg-gray-500'
                                  }`}
                                title={`${booking.status} - ${format(parseISO(booking.startTime), 'HH:mm')}`}
                              >
                                {format(parseISO(booking.startTime), 'HH:mm')}
                              </div>
                            ))}
                            {dayBookings.length > 2 && (
                              <div className="text-xs text-gray-500">
                                +{dayBookings.length - 2} more
                              </div>
                            )}
                          </div>
                        )}

                        {/* Blocked slot indicator */}
                        {isBooked && dayBookings.length === 0 && (
                          <div className="absolute top-1 right-1">
                            <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              </div>

              {/* Booking Details for Current Month */}
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-4">
                  Bookings for {format(currentMonth, 'MMMM yyyy')}
                </h4>
                <div className="space-y-3">
                  {bookings
                    .filter(booking => {
                      const bookingDate = parseISO(booking.date)
                      return isSameMonth(bookingDate, currentMonth)
                    })
                    .sort((a, b) => new Date(a.date) - new Date(b.date))
                    .map((booking) => (
                      <div key={booking.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center space-x-3">
                          <div className={`w-3 h-3 rounded-full ${booking.status?.toLowerCase() === 'requested' ? 'bg-yellow-500' :
                            booking.status?.toLowerCase() === 'confirmed' ? 'bg-green-500' :
                              booking.status?.toLowerCase() === 'completed' ? 'bg-blue-500' :
                                booking.status?.toLowerCase() === 'pending' ? 'bg-orange-500' :
                                  booking.status?.toLowerCase() === 'cancelled' ? 'bg-red-500' :
                                    'bg-gray-500'
                            }`}></div>
                          <div>
                            <p className="font-medium text-gray-900">
                              {format(parseISO(booking.date), 'MMM dd, yyyy')}
                            </p>
                            <p className="text-sm text-gray-600">
                              {format(parseISO(booking.startTime), 'HH:mm')} - {format(parseISO(booking.endTime), 'HH:mm')}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="space-y-1">
                            <p className={`text-sm font-medium capitalize ${booking.status?.toLowerCase() === 'requested' ? 'text-yellow-600' :
                              booking.status?.toLowerCase() === 'confirmed' ? 'text-green-600' :
                                booking.status?.toLowerCase() === 'completed' ? 'text-blue-600' :
                                  booking.status?.toLowerCase() === 'pending' ? 'text-orange-600' :
                                    booking.status?.toLowerCase() === 'cancelled' ? 'text-red-600' :
                                      'text-gray-600'
                              }`}>
                              {booking.status}
                            </p>
                            {booking.paymentStatus && (
                              <p className={`text-xs font-medium ${booking.paymentStatus?.toLowerCase() === 'paid' ? 'text-green-600' :
                                booking.paymentStatus?.toLowerCase() === 'pending' ? 'text-yellow-600' :
                                  'text-red-600'
                                }`}>
                                Payment: {booking.paymentStatus}
                              </p>
                            )}
                            {booking.totalAmount && (
                              <p className="text-xs font-medium text-gray-900">
                                ₹{booking.totalAmount}
                              </p>
                            )}
                          </div>
                          <p className="text-xs text-gray-500 mt-1">
                            {booking.notes || booking.slot || 'No notes'}
                          </p>
                        </div>
                      </div>
                    ))}

                  {bookings.filter(booking => {
                    const bookingDate = parseISO(booking.date)
                    return isSameMonth(bookingDate, currentMonth)
                  }).length === 0 && (
                      <p className="text-gray-500 text-center py-4">
                        No bookings for this month
                      </p>
                    )}
                </div>
              </div>

              {/* Blocked Slots Information */}
              {bookedSlots.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <h4 className="font-semibold text-red-900 mb-3">Blocked Time Slots</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {bookedSlots.map((slot, index) => {
                      const [startTime, endTime] = slot.split('-')
                      return (
                        <div key={index} className="flex items-center justify-between p-2 bg-red-100 rounded">
                          <span className="text-sm text-red-800">
                            {format(parseISO(startTime), 'MMM dd, yyyy')}
                          </span>
                          <span className="text-sm font-medium text-red-900">
                            {format(parseISO(startTime), 'HH:mm')} - {format(parseISO(endTime), 'HH:mm')}
                          </span>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'reviews' && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Customer Reviews</h3>
              <div className="space-y-4">
                {(!pandit.reviews || pandit.reviews.length === 0) ? (
                  <div className="text-center py-8">
                    <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">No reviews yet</p>
                  </div>
                ) : (
                  pandit.reviews.map((review) => (
                    <div key={review.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center space-x-3">
                          <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden">
                            {review.user?.image ? (
                              <img src={review.user.image} alt={review.user.name} className="h-full w-full object-cover" />
                            ) : (
                              <User className="h-6 w-6 text-gray-500" />
                            )}
                          </div>
                          <div>
                            <h4 className="font-medium text-gray-900">{review.user?.name || 'Anonymous'}</h4>
                            <p className="text-xs text-gray-500">{format(new Date(review.createdAt), 'MMM dd, yyyy')}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="flex items-center mb-1">
                            {[...Array(5)].map((_, i) => (
                              <Star
                                key={i}
                                className={`w-4 h-4 ${i < review.rating ? 'text-yellow-400 fill-current' : 'text-gray-300'}`}
                              />
                            ))}
                          </div>
                        </div>
                      </div>
                      <p className="text-gray-700 text-sm mt-2">{review.comment}</p>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    {/* Rejection Modal */ }
    {isRejectModalOpen && (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div className="absolute inset-0 bg-gray-500 opacity-75" onClick={() => setIsRejectModalOpen(false)}></div>
        </div>

        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="sm:flex sm:items-start">
              <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                <AlertCircle className="h-6 w-6 text-red-600" aria-hidden="true" />
              </div>
              <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                  Reject Pandit Profile
                </h3>
                <div className="mt-2">
                  <p className="text-sm text-gray-500 mb-4">
                    Are you sure you want to reject this pandit? This will suspend their account and send them a notification.
                  </p>
                  <textarea
                    className="w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-red-500 focus:border-red-500"
                    rows="4"
                    placeholder="Enter reason for rejection..."
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                  ></textarea>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
              onClick={handleRejectPandit}
              disabled={rejectingPandit}
            >
              {rejectingPandit ? 'Rejecting...' : 'Reject Pandit'}
            </button>
            <button
              type="button"
              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
              onClick={() => setIsRejectModalOpen(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  )}
</div>
  )}
export default PanditDetails
